import { MyJoinRequests } from '../components/join/JoinTeam';
import { ClubTeamInvites } from '../components/clubs/TeamClub';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, Plus, ShieldCheck, X } from 'lucide-react';
import type { TeamDetail, TeamSummary } from '@hoopify/shared';
import { api, errorMessage, fieldErrors } from '../lib/api';
import { useMe } from '../lib/session';
import { Alert, Button, Dialog, Empty, Field, LoadingBlock, PageHeader, RoleBadge } from '../components/ui';

export function TeamsPage() {
  const me = useMe();
  const [creating, setCreating] = useState(false);
  const teams = useQuery({ queryKey: ['teams'], queryFn: () => api.get<TeamSummary[]>('/teams') });

  const newTeam = (
    <Button onClick={() => setCreating(true)} disabled={!me.emailVerified} title={me.emailVerified ? undefined : 'Confirm your email first'}>
      <Plus className="size-4" aria-hidden /> New team
    </Button>
  );

  return (
    <>
      <PageHeader title="Teams" description="Each team has its own coaches, players and training sessions." actions={teams.data?.length ? newTeam : undefined} />
      <MyJoinRequests />
      {teams.data && <ClubTeamInvites teams={teams.data} />}
      {!me.mfaEnabled && <MfaNudge />}
      {teams.isLoading ? (
        <LoadingBlock />
      ) : teams.isError ? (
        <Alert>{errorMessage(teams.error)}</Alert>
      ) : !teams.data?.length ? (
        <Empty title="You’re not on a team yet" action={newTeam}>
          Create a team to start planning sessions. If you’re an assistant, player or parent, ask your coach to invite you.
        </Empty>
      ) : (
        <ul className="divide-y divide-line overflow-hidden rounded-lg border border-line bg-surface">
          {teams.data.map((t) => (
            <li key={t.id}>
              <Link to={`/teams/${t.id}`} className="flex items-center gap-4 px-4 py-3.5 hover:bg-sunken sm:px-5">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
                    <span className="font-medium">{t.name}</span>
                    <RoleBadge role={t.role} />
                  </div>
                  <p className="mt-0.5 text-[13px] text-muted">
                    {[t.ageGroup, t.season, t.clubName, `${t.memberCount} ${t.memberCount === 1 ? 'member' : 'members'}`].filter(Boolean).join(', ')}
                  </p>
                </div>
                <ChevronRight className="size-4 shrink-0 text-faint" aria-hidden />
              </Link>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-8 text-[14px] text-muted">
        Run a club with lots of teams?{' '}
        <Link to="/clubs/register" className="font-medium text-action hover:underline">
          Register your club
        </Link>{' '}
        to set up its teams, coaches and players in one place.
      </p>
      <CreateTeamDialog open={creating} onClose={() => setCreating(false)} />
    </>
  );
}

function CreateTeamDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [ageGroup, setAgeGroup] = useState('');
  const [season, setSeason] = useState('');
  const create = useMutation({
    mutationFn: () => api.post<TeamDetail>('/teams', { name, ageGroup, season }),
    onSuccess: async (team) => {
      await qc.invalidateQueries({ queryKey: ['teams'] });
      navigate(`/teams/${team.id}`);
    },
  });
  const fields = fieldErrors(create.error);
  const close = () => {
    create.reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={close} title="New team">
      <form onSubmit={(e: FormEvent) => { e.preventDefault(); create.mutate(); }} className="space-y-4" noValidate>
        {create.isError && !Object.keys(fields).length && <Alert>{errorMessage(create.error)}</Alert>}
        <Field label="Team name" value={name} onChange={(e) => setName(e.target.value)} error={fields.name} placeholder="e.g. Hawks U14 Girls" required autoFocus />
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Age group" value={ageGroup} onChange={(e) => setAgeGroup(e.target.value)} error={fields.ageGroup} placeholder="Optional, e.g. U14" />
          <Field label="Season" value={season} onChange={(e) => setSeason(e.target.value)} error={fields.season} placeholder="Optional, e.g. Winter 2026" />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button type="submit" loading={create.isPending}>
            Create team
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

const NUDGE_KEY = 'hoopify.mfaNudgeHidden';

/** Two-step sign-in is optional but preferred, so we suggest it until it's on or dismissed. */
function MfaNudge() {
  const [hidden, setHidden] = useState(() => {
    try {
      return localStorage.getItem(NUDGE_KEY) === '1';
    } catch {
      return false;
    }
  });
  if (hidden) return null;
  const hide = () => {
    setHidden(true);
    try {
      localStorage.setItem(NUDGE_KEY, '1');
    } catch {
      /* fine: it'll show again next time */
    }
  };
  return (
    <div className="mb-6 flex items-start gap-3 rounded-lg border border-line bg-surface px-4 py-3">
      <ShieldCheck className="mt-0.5 size-5 shrink-0 text-action" aria-hidden />
      <p className="flex-1 text-[14px]">
        <span className="font-medium">Protect your account with two-step sign-in.</span>{' '}
        <span className="text-muted">It takes a minute with an authenticator app on your phone.</span>{' '}
        <Link to="/account" className="font-medium whitespace-nowrap text-action hover:underline">
          Set it up
        </Link>
      </p>
      <button className="btn-ghost -m-1 p-1.5" onClick={hide} aria-label="Hide this suggestion">
        <X className="size-4" />
      </button>
    </div>
  );
}
